import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { KeyRound, LogOut, Pencil, Plus, Power, Scale } from "lucide-react";
import { adminClient, clearToken, formatApiError } from "@/admin/api";
import CampaignEditor from "@/components/admin/CampaignEditor";
import ChangePassword from "@/components/admin/ChangePassword";
import LeadsViewer from "@/components/admin/LeadsViewer";

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [editor, setEditor] = useState(null); // null | { campaign?: object }
  const [tab, setTab] = useState("campaigns"); // campaigns | leads
  const [showPwd, setShowPwd] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await adminClient().get("/admin/campaigns");
      setCampaigns(data.campaigns || []);
      setError("");
    } catch (err) {
      if (err.response?.status === 401) {
        clearToken();
        navigate("/admin/login");
        return;
      }
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    load();
  }, [load]);

  const toggle = async (c) => {
    try {
      await adminClient().patch(`/admin/campaigns/${c.id}/status`, {
        active: !c.active,
      });
      load();
    } catch (err) {
      if (err.response?.status === 401) {
        clearToken();
        navigate("/admin/login");
        return;
      }
      setError(formatApiError(err.response?.data?.detail));
    }
  };

  const logout = () => {
    clearToken();
    navigate("/admin/login");
  };

  return (
    <main data-testid="admin-dashboard" className="min-h-screen bg-[#FBF9F5]">
      <header className="border-b border-[#E7E2DA] bg-[#FBF9F5]">
        <div className="mx-auto max-w-6xl px-5 sm:px-8 h-20 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="flex h-9 w-9 items-center justify-center border border-[#1A1715] rounded-sm">
              <Scale className="h-4 w-4" strokeWidth={1.75} />
            </span>
            <div>
              <span className="block font-serif font-semibold text-lg text-[#1A1715]">
                Campaign Admin
              </span>
              <span className="block font-mono text-[10px] uppercase tracking-[0.2em] text-[#78716C]">
                lawsuitsettlementhelp.com
              </span>
            </div>
          </div>
          <div className="flex items-center gap-5">
            <button
              type="button"
              data-testid="change-password-button"
              onClick={() => setShowPwd(true)}
              className="inline-flex items-center gap-2 font-sans text-sm font-semibold text-[#44403C] hover:text-[#C2410C] transition-colors"
            >
              <KeyRound className="h-4 w-4" /> Change Password
            </button>
            <button
              type="button"
              data-testid="admin-logout-button"
              onClick={logout}
              className="inline-flex items-center gap-2 font-sans text-sm font-semibold text-[#44403C] hover:text-[#C2410C] transition-colors"
            >
              <LogOut className="h-4 w-4" /> Sign out
            </button>
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-6xl px-5 sm:px-8 py-10">
        {error && (
          <p
            data-testid="admin-error-banner"
            role="alert"
            className="mb-6 border border-[#FECACA] bg-[#FEF2F2] rounded-sm px-4 py-3 font-sans text-sm font-medium text-[#B91C1C]"
          >
            {error}
          </p>
        )}

        <div
          data-testid="admin-tabs"
          className="mb-8 flex items-center gap-6 border-b border-[#E7E2DA]"
        >
          {[
            ["campaigns", "Campaigns"],
            ["leads", "Leads"],
          ].map(([key, label]) => (
            <button
              key={key}
              type="button"
              data-testid={`tab-${key}`}
              onClick={() => {
                setTab(key);
                setEditor(null);
              }}
              className={`pb-3 -mb-px font-sans text-sm font-semibold border-b-2 transition-colors ${
                tab === key
                  ? "border-[#C2410C] text-[#1A1715]"
                  : "border-transparent text-[#78716C] hover:text-[#1A1715]"
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        {tab === "leads" && (
          <LeadsViewer
            onUnauthorized={() => {
              clearToken();
              navigate("/admin/login");
            }}
          />
        )}

        {tab === "campaigns" && (editor ? (
          <CampaignEditor
            initial={editor.campaign || null}
            onCancel={() => setEditor(null)}
            onSaved={() => {
              setEditor(null);
              load();
            }}
          />
        ) : (
          <>
            <div className="flex items-center justify-between">
              <div>
                <h1 className="font-serif font-semibold text-2xl text-[#1A1715]">
                  Campaigns
                </h1>
                <p className="mt-1 font-sans text-sm text-[#78716C]">
                  Only active campaigns appear on the public lander.
                  Deactivation preserves all historical leads.
                </p>
              </div>
              <button
                type="button"
                data-testid="new-campaign-button"
                onClick={() => setEditor({})}
                className="inline-flex items-center gap-2 bg-[#C2410C] text-[#FBF9F5] font-sans font-semibold text-sm px-5 py-3 rounded-sm transition-colors duration-300 hover:bg-[#9A3412]"
              >
                <Plus className="h-4 w-4" /> New Campaign
              </button>
            </div>

            <div className="mt-8 bg-[#FFFFFF] border border-[#E7E2DA] rounded-sm overflow-x-auto">
              <table className="w-full min-w-[640px] text-left">
                <thead>
                  <tr className="border-b border-[#E7E2DA]">
                    {["Campaign", "ID", "Category", "Status", "Last updated", ""].map(
                      (h) => (
                        <th
                          key={h}
                          className="px-5 py-3.5 font-mono text-[10px] uppercase tracking-[0.18em] text-[#78716C] font-semibold"
                        >
                          {h}
                        </th>
                      )
                    )}
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td
                        colSpan={6}
                        data-testid="campaigns-loading"
                        className="px-5 py-10 text-center font-sans text-sm text-[#78716C]"
                      >
                        Loading campaigns…
                      </td>
                    </tr>
                  ) : (
                    campaigns.map((c) => (
                      <tr
                        key={c.id}
                        data-testid={`campaign-row-${c.id}`}
                        className="border-b border-[#ECE7DF] last:border-0"
                      >
                        <td className="px-5 py-4 font-serif font-semibold text-base text-[#1A1715]">
                          {c.name}
                        </td>
                        <td className="px-5 py-4 font-mono text-xs text-[#78716C]">
                          {c.id}
                        </td>
                        <td className="px-5 py-4 font-sans text-sm text-[#44403C]">
                          {c.category}
                        </td>
                        <td className="px-5 py-4">
                          <span
                            data-testid={`campaign-status-${c.id}`}
                            className={`inline-block font-mono text-[10px] uppercase tracking-[0.14em] px-2.5 py-1 rounded-sm ${
                              c.active
                                ? "bg-[#F0FDF4] text-[#166534]"
                                : "bg-[#F4EFEA] text-[#78716C]"
                            }`}
                          >
                            {c.active ? "Active" : "Inactive"}
                          </span>
                        </td>
                        <td className="px-5 py-4 font-sans text-xs text-[#78716C]">
                          {c.updated_at
                            ? new Date(c.updated_at).toLocaleDateString("en-US", {
                                year: "numeric",
                                month: "short",
                                day: "numeric",
                              })
                            : "—"}
                        </td>
                        <td className="px-5 py-4">
                          <div className="flex items-center gap-2 justify-end">
                            <button
                              type="button"
                              data-testid={`edit-campaign-${c.id}`}
                              onClick={() => setEditor({ campaign: c })}
                              className="inline-flex items-center gap-1.5 border border-[#E7E2DA] rounded-sm px-3 py-2 font-sans text-xs font-semibold text-[#44403C] hover:border-[#C2410C] hover:text-[#C2410C] transition-colors"
                            >
                              <Pencil className="h-3.5 w-3.5" /> Edit
                            </button>
                            <button
                              type="button"
                              data-testid={`toggle-campaign-${c.id}`}
                              onClick={() => toggle(c)}
                              className={`inline-flex items-center gap-1.5 border rounded-sm px-3 py-2 font-sans text-xs font-semibold transition-colors ${
                                c.active
                                  ? "border-[#E7E2DA] text-[#44403C] hover:border-[#B91C1C] hover:text-[#B91C1C]"
                                  : "border-[#E7E2DA] text-[#44403C] hover:border-[#166534] hover:text-[#166534]"
                              }`}
                            >
                              <Power className="h-3.5 w-3.5" />
                              {c.active ? "Deactivate" : "Activate"}
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </>
        ))}

        {showPwd && (
          <ChangePassword
            onClose={() => setShowPwd(false)}
            onChanged={() => {
              clearToken();
              navigate("/admin/login");
            }}
          />
        )}
      </div>
    </main>
  );
}
